import React, {useContext, useEffect, useMemo, useRef, useState} from "react";
import moment from "moment";
import locale from "antd/lib/locale/he_IL";
import classNames from "classnames";
import {ConfigProvider, DatePicker, Form, Select} from "antd";
import {DownOutlined} from "@ant-design/icons";
import {
    DEFAULT_DELIVERY_ARRIVING_TIME_RANGE,
    DEFAULT_DELIVERY_MIN_NOTICE,
    DEFAULT_MINIMUM_ORDER_NOTICE_HOURS,
    DISABLED_DATE_ERROR,
    ERROR_LEVEL,
    hebDayTranslate,
    OUT_OF_BUSINESS_HOURS_ERROR,
    OUT_OF_DELIVERY_PROVIDER_BUSINESS_HOURS_ERROR,
    SYSTEM_MINIMUM_ORDER_NOTICE_HOURS,
    TIME_ERROR,
    WARN_LEVEL,
    WeekdaysSorter
} from "../MarketplaceUtils.jsx";
import {DeliveryDisclaimers} from "../DeliveryDisclaimers.jsx";
import {MarketplaceBookingContext} from "../MarketplaceBooking/MarketplaceBookingContext.jsx";
import {CustomTimePicker} from "../../components/index.jsx";
import {isProductHasShippingProvider} from "../../admin/upload/utils.js";
import {formatHebrewTimeString} from "../../utils/DateUtils.jsx";
import {
    CategoryToWhenSectionNoticeTitle,
    productDefaultNoticeTitle,
    CategoryToTimeRangePlaceholder,
    timeRangeDefaultPlaceholder
} from "../consts.jsx";
import "./order-form-time-panel.css";
import isMobileDevice from "is-mobile";
import {useWindowDimensions} from "../../utils/hooks.jsx";
import {
    DATE_PICKER_STYLE,
    SYSTEM_DEFAULT_START_TIME,
    SYSTEM_DEFAULT_END_TIME,
    LONG_RANGE_HOURS,
    TWO_DAY_RANGE_HOURS
} from "./consts";
import {
    calculateTimeRanges,
    formatSelectedTimeRange,
    formatMinNoticeText,
    isDayAvailable,
    isTimeInProviderBusinessHours,
    isTimeInNextDayAvailability,
    isTimeInShippingDeliveryBusinessHours,
    findMatchingTimeRange
} from "./utils.js";

export const OrderFormTimePanel = ({
    product,
    providerDetails,
    allowPastDates,
    deliveryOptions,
    formValues,
    onFormValueChange,
    deliveryMaxTime,
    businessStartHourForAdmin,
    businessStartMinuteForAdmin
}) => {
    const {width} = useWindowDimensions();
    const isMobileScreen = useMemo(() => width <= 425, [width]);
    const {deliveryAreaProperties} = useContext(MarketplaceBookingContext);
    const [showDeliveryDisclaimers, setShowDeliveryDisclaimers] = useState(null);
    const deliveryArrivingTimeRange = useMemo(
        () => deliveryOptions?.arrivingTimeRange ?? DEFAULT_DELIVERY_ARRIVING_TIME_RANGE,
        [deliveryOptions?.arrivingTimeRange]
    );

    const isLongRange = useMemo(() => deliveryArrivingTimeRange >= LONG_RANGE_HOURS, [deliveryArrivingTimeRange]);
    const isTwoDayRange = useMemo(() => deliveryArrivingTimeRange >= TWO_DAY_RANGE_HOURS, [deliveryArrivingTimeRange]);
    const isShortRange = useMemo(() => deliveryArrivingTimeRange < LONG_RANGE_HOURS, [deliveryArrivingTimeRange]);

    const DEFAULT_START_TIME = useMemo(() => {
        // For long range or two-day range, use provider's start hour if available
        if ((isLongRange || isTwoDayRange) && providerDetails?.availability && formValues?.date) {
            const dayOfWeek = moment(formValues.date).format("dddd").toLowerCase();
            const dayAvailability = providerDetails.availability[dayOfWeek];

            if (dayAvailability?.selected && dayAvailability?.start) {
                const [startHour, startMinute] = dayAvailability.start.split(":").map(Number);
                const businessStartHour = startHour;
                const businessStartMinute = startMinute;
                return moment().set({hour: businessStartHour, minute: businessStartMinute, second: 0, millisecond: 0});
            }
        }
        return moment(SYSTEM_DEFAULT_START_TIME, "HH:mm");
    }, [isLongRange, isTwoDayRange, providerDetails?.availability, formValues?.date]);
    const DEFAULT_END_TIME = useMemo(() => moment(SYSTEM_DEFAULT_END_TIME, "HH:mm"), []);

    const timeRanges = useMemo(
        () =>
            calculateTimeRanges(
                isShortRange,
                providerDetails?.availability,
                formValues?.date,
                deliveryArrivingTimeRange,
                businessStartHourForAdmin,
                businessStartMinuteForAdmin
            ),
        [
            isShortRange,
            providerDetails?.availability,
            formValues?.date,
            deliveryArrivingTimeRange,
            businessStartHourForAdmin,
            businessStartMinuteForAdmin
        ]
    );

    const selectedTimeRange = useMemo(
        () => findMatchingTimeRange(isShortRange, formValues?.time, formValues?.untilTime, timeRanges),
        [isShortRange, formValues?.time, formValues?.untilTime, timeRanges]
    );

    useEffect(() => {
        if (isShortRange && selectedTimeRange && formValues?.timeRange !== selectedTimeRange) {
            onFormValueChange("timeRange", selectedTimeRange);
        }
    }, [isShortRange, selectedTimeRange, formValues?.timeRange, onFormValueChange]);

    const minNoticeText = useMemo(() => formatMinNoticeText(deliveryOptions?.minNotice), [deliveryOptions?.minNotice]);

    useEffect(() => {
        if (!isLongRange || !formValues?.date) return;

        const currentTime = formValues?.time;
        const needsStartTime = !currentTime || !moment(currentTime).isSame(DEFAULT_START_TIME);
        if (needsStartTime) {
            onFormValueChange("time", DEFAULT_START_TIME.clone());
        }

        const currentUntilTime = formValues?.untilTime;
        const needsEndTime = !currentUntilTime || !moment(currentUntilTime).isSame(DEFAULT_END_TIME);
        if (needsEndTime) {
            onFormValueChange("untilTime", DEFAULT_END_TIME.clone());
        }

        const currentUntilDate = formValues?.untilDate;
        if (!currentUntilDate) {
            if (isTwoDayRange) {
                // Add deliveryArrivingTimeRange hours, then subtract 1 day and set to 18:00
                const calculatedUntilDate = moment(formValues.date)
                    .add(deliveryArrivingTimeRange, "hours")
                    .subtract(1, "day")
                    .set({hour: 18, minute: 0, second: 0, millisecond: 0});
                onFormValueChange("untilDate", calculatedUntilDate);
            } else {
                onFormValueChange("untilDate", moment(formValues.date));
            }
        }
    }, [formValues?.date, isLongRange, isTwoDayRange, deliveryArrivingTimeRange]);

    useEffect(() => {
        const {date, time} = formValues;

        if (!date) {
            onFormValueChange(`errors.${TIME_ERROR}`, null);
            return;
        }

        const effectiveTime = isLongRange && !time ? DEFAULT_START_TIME : time;
        const selectedTime = moment(date).set({
            hour: effectiveTime?.hours() ?? DEFAULT_START_TIME.hours(),
            minute: effectiveTime?.minutes() ?? DEFAULT_START_TIME.minutes()
        });

        const minNotice =
            deliveryAreaProperties?.minNotice ?? deliveryOptions?.minNotice ?? DEFAULT_MINIMUM_ORDER_NOTICE_HOURS;
        const minNoticeDate = moment().add(minNotice, "hours");

        const effectiveDeliveryMaxTime =
            deliveryMaxTime ?? moment(selectedTime).add(deliveryArrivingTimeRange, "hours");

        const disabledDate = product.disabledDates?.find(({from, to}) =>
            moment(date).isBetween(from, to, "date", "[]")
        );

        let errorMessage = null;
        let errorLevel = null;

        if ((!time || !date) && !isLongRange) {
            errorMessage = null;
        } else if (selectedTime.isBefore(moment().add(SYSTEM_MINIMUM_ORDER_NOTICE_HOURS, "hours"))) {
            errorMessage = `לא ניתן להזמין מוצר בהתראה של פחות מ-${SYSTEM_MINIMUM_ORDER_NOTICE_HOURS} שעות`;
            errorLevel = ERROR_LEVEL;
        } else if (disabledDate) {
            errorMessage = DISABLED_DATE_ERROR.replace(
                "{DISABLED_DATES}",
                `${moment(disabledDate.from).format("DD/MM")} עד ${moment(disabledDate.to).format("DD/MM")} `
            );
            errorLevel = ERROR_LEVEL;
            // For long/two-day ranges, only check if the day is available, not the hours
        } else if ((isLongRange || isTwoDayRange) && !isDayAvailable(selectedTime, providerDetails?.availability)) {
            errorMessage = OUT_OF_BUSINESS_HOURS_ERROR;
            errorLevel = ERROR_LEVEL;
        } else if (
            !isLongRange &&
            !isTwoDayRange &&
            !isTimeInProviderBusinessHours(selectedTime, effectiveDeliveryMaxTime, providerDetails?.availability)
        ) {
            errorMessage = OUT_OF_BUSINESS_HOURS_ERROR;
            errorLevel = ERROR_LEVEL;
        } else if (!isTimeInNextDayAvailability(selectedTime, providerDetails?.nextDayAvailability)) {
            errorMessage = OUT_OF_DELIVERY_PROVIDER_BUSINESS_HOURS_ERROR;
            errorLevel = ERROR_LEVEL;
        } else if (isProductHasShippingProvider(product) && !isTimeInShippingDeliveryBusinessHours(selectedTime)) {
            errorMessage = OUT_OF_DELIVERY_PROVIDER_BUSINESS_HOURS_ERROR;
            errorLevel = ERROR_LEVEL;
        } else if (selectedTime.isBefore(minNoticeDate)) {
            errorMessage = ` יש לשים לב כי זמן ההתראה הרגיל הדרוש למוצר הוא${minNoticeText}. עם זאת בקשתכם תועבר לספק לבדיקת היתכנות זמן התראה קצר יותר.`;
            errorLevel = WARN_LEVEL;
        }

        onFormValueChange(`errors.${TIME_ERROR}`, errorMessage ? {message: errorMessage, level: errorLevel} : null);
    }, [
        formValues?.date,
        formValues?.time,
        formValues?.untilTime,
        formValues?.untilDate,
        providerDetails?.availability,
        providerDetails?.nextDayAvailability,
        deliveryArrivingTimeRange,
        product?.disabledDates,
        deliveryAreaProperties,
        deliveryOptions?.minNotice,
        isLongRange,
        minNoticeText
    ]);

    const noticeTitle = formatHebrewTimeString(moment.duration(deliveryArrivingTimeRange, "hours"));

    return (
        <>
            <div className="marketplace-booking-time-date-container">
                <ConfigProvider direction="rtl" locale={locale}>
                    <div className="date-form-container">
                        <div className="date-form-container-sub-title">
                            <span>
                                {CategoryToWhenSectionNoticeTitle[product?.services?.[0]] ?? productDefaultNoticeTitle}
                            </span>
                            <span className="date-form-container-total-time">{noticeTitle}</span>
                        </div>
                        <div className="date-form">
                            <Form.Item name="date">
                                <DatePicker
                                    popupClassName={classNames("order-form-time-panel-date-picker", {
                                        mobile: isMobileDevice() || isMobileScreen
                                    })}
                                    inputReadOnly={true}
                                    style={DATE_PICKER_STYLE}
                                    suffixIcon={null}
                                    onChange={momentDate => {
                                        onFormValueChange("date", momentDate);
                                        if (isLongRange && momentDate) {
                                            onFormValueChange("time", DEFAULT_START_TIME.clone());
                                            if (!isTwoDayRange) {
                                                onFormValueChange("untilDate", momentDate);
                                            }
                                        }
                                        if (isTwoDayRange && momentDate) {
                                            // Add deliveryArrivingTimeRange hours, then subtract 1 day and set to 18:00
                                            const calculatedUntilDate = momentDate
                                                .clone()
                                                .add(deliveryArrivingTimeRange, "hours")
                                                .subtract(1, "day")
                                                .set({hour: 18, minute: 0, second: 0, millisecond: 0});
                                            if (
                                                !formValues?.untilDate ||
                                                !moment(formValues.untilDate).isSame(calculatedUntilDate, "day")
                                            ) {
                                                onFormValueChange("untilDate", calculatedUntilDate);
                                            }
                                        }
                                    }}
                                    format="DD MMM, YYYY"
                                    placeholder={"בחירת תאריך"}
                                    disabledDate={date =>
                                        !allowPastDates && date.isBefore(moment().subtract(1, "days"))
                                    }
                                />
                            </Form.Item>

                            {isShortRange && (
                                <Form.Item name="timeRange">
                                    <Select
                                        className="order-form-time-panel-time-range-select"
                                        dropdownStyle={{textAlign: "center", letterSpacing: "0.05em"}}
                                        placeholder={
                                            CategoryToTimeRangePlaceholder[product?.services?.[0]] ??
                                            timeRangeDefaultPlaceholder
                                        }
                                        onChange={value => {
                                            const selectedRange = timeRanges.find(r => r.value === value);
                                            if (selectedRange) {
                                                onFormValueChange("time", selectedRange.startTime);
                                                onFormValueChange("untilTime", selectedRange.endTime);
                                                onFormValueChange("timeRange", value);
                                            }
                                        }}
                                        options={timeRanges.map(range => ({
                                            label: range.label,
                                            value: range.value
                                        }))}
                                        showSearch={false}
                                        allowClear={false}
                                    />
                                </Form.Item>
                            )}

                            {isTwoDayRange && (
                                <>
                                    <span>עד</span>
                                    <Form.Item name="untilDate">
                                        <DatePicker
                                            popupClassName={classNames("order-form-time-panel-date-picker", {
                                                mobile: isMobileDevice() || isMobileScreen
                                            })}
                                            inputReadOnly={true}
                                            style={DATE_PICKER_STYLE}
                                            suffixIcon={null}
                                            onChange={momentDate => {
                                                onFormValueChange("untilDate", momentDate);
                                                if (isTwoDayRange && momentDate) {
                                                    // Reverse calculation: add 1 day, subtract deliveryArrivingTimeRange hours, set to 9:00
                                                    const calculatedStartDate = momentDate
                                                        .clone()
                                                        .add(1, "day")
                                                        .subtract(deliveryArrivingTimeRange, "hours")
                                                        .set({hour: 9, minute: 0, second: 0, millisecond: 0});
                                                    if (
                                                        !formValues?.date ||
                                                        !moment(formValues.date).isSame(calculatedStartDate, "day")
                                                    ) {
                                                        onFormValueChange("date", calculatedStartDate);
                                                    }
                                                }
                                            }}
                                            format="DD MMM, YYYY"
                                            placeholder={"בחירת תאריך סיום"}
                                            disabledDate={date => {
                                                if (!allowPastDates && date.isBefore(moment().subtract(1, "days"))) {
                                                    return true;
                                                }
                                                if (formValues?.date) {
                                                    // Calculate expected end date: startDate + deliveryArrivingTimeRange hours - 1 day
                                                    const minEndDate = moment(formValues.date)
                                                        .add(deliveryArrivingTimeRange, "hours")
                                                        .subtract(1, "day");
                                                    return date.isBefore(minEndDate, "day");
                                                }
                                                return false;
                                            }}
                                            value={
                                                formValues?.untilDate ||
                                                (formValues?.date
                                                    ? moment(formValues.date)
                                                          .add(deliveryArrivingTimeRange, "hours")
                                                          .subtract(1, "day")
                                                          .set({hour: 18, minute: 0, second: 0, millisecond: 0})
                                                    : null)
                                            }
                                        />
                                    </Form.Item>
                                </>
                            )}
                        </div>
                    </div>
                </ConfigProvider>
            </div>
            <div
                onClick={() => setShowDeliveryDisclaimers(prev => !prev)}
                className="marketplace-booking-delivery-disclaimer">
                {"לצפייה "}
                <u>בתנאי אספקה ושילוח</u>
                <DownOutlined
                    className={classNames("marketplace-booking-delivery-disclaimer-icon", {
                        active: showDeliveryDisclaimers
                    })}
                />
                {showDeliveryDisclaimers ? (
                    <DeliveryDisclaimers
                        product={product}
                        deliveryOptions={deliveryOptions}
                        minNoticeText={minNoticeText}
                    />
                ) : null}
            </div>
            {product && providerDetails.availability ? (
                <div>
                    <div style={{color: "#8398A6", fontSize: "16px"}}>שעות פעילות בית העסק</div>
                    {WeekdaysSorter(Object.keys(providerDetails.availability)).map(day =>
                        providerDetails.availability[day].selected ? (
                            <div
                                key={day}
                                style={{
                                    color: "#8398A6",
                                    fontSize: "16px"
                                }}>
                                {providerDetails.availability[day].start} - {providerDetails.availability[day].end} |{" "}
                                {hebDayTranslate(day)}
                            </div>
                        ) : null
                    )}
                </div>
            ) : null}
        </>
    );
};
